/* eslint-disable import/prefer-default-export -- single-purpose helper, may grow */
import { ioTrack } from '@/utils/ioTrack';

/**
 * Frontend funnel tracking for the order-creation wizard (Bestellprozess), IOSUITE-7337.
 *
 * All events use category `order.create`. The funnel is segmented via custom dimensions:
 *   - `module`      = origin app: 'order' (io.order backoffice) | 'sales' (sales wizard, backend
 *                     or partner login) | 'center' (customer portal). Only the suite-backend
 *                     property carries a `module` dimension; the `center`/`sales` properties drop
 *                     it (it is constant there), which the tracking client does silently.
 *   - `occasion`    = the wizard flow mode: 'new' | 'edit' | 'tariff_change' | 'relocation'
 *                     (renamed from `functional-area` in schema v0.29).
 *   - `object-uuid` = attached only at `tariff_calculated`/`tariff_selected`: the PRODUCT (Tarif)
 *                     uuid, so tariff performance joins across io.product / io.order / io.center.
 *                     Passed by the call site via the `dims` argument.
 *
 * NAME CONVENTION (schema v0.36.0): every event's name is `<detail>|<occasion>|<origin>`.
 * io.insight cannot count events per custom-dimension value (a Matomo segment selects VISITS,
 * not actions), but it splits the event name exactly. So occasion and origin travel in the name
 * as well — that is what lets the Bestellprozess funnel be split into io.order / io.sales /
 * io.center. `<detail>` is the event's own value (step, tariff slug, Abschlusskanal, …) and may
 * be empty; the separator `|` must not occur in any part.
 *
 * The object TYPE is no longer sent as a dimension — it lives at each event as the static
 * `objectType` schema field. Context is read from `window.ROUTER`, which the mount blade sets and
 * the router re-mirrors on every navigation. Safe no-op if the tracker or context is absent.
 */

interface OrderRouterCtx {
  basePath?: string;
  isSales?: boolean;
  orderMode?: string;
  meta?: { mode?: string; isSales?: boolean };
}

interface WizardCtx {
  occasion: string;
  module: string;
}

function wizardContext(): WizardCtx {
  const w = window as unknown as { ROUTER?: OrderRouterCtx; CONTRACT_DATA?: unknown };
  const r = w.ROUTER ?? {};

  // io.center customer portal mounts with basePath '/contracts' and injects CONTRACT_DATA.
  const isCenter = r.basePath === '/contracts' || w.CONTRACT_DATA != null;

  const mode = r.meta?.mode; // kebab: 'create' | 'edit' | 'tariff-change' | 'relocation'
  let occasion = 'new';
  if (mode === 'tariff-change' || r.orderMode === 'tariffChange') occasion = 'tariff_change';
  else if (mode === 'relocation' || r.orderMode === 'relocation') occasion = 'relocation';
  else if (mode === 'edit') occasion = 'edit';

  // The blade sets `isSales` on window.ROUTER, but the router's beforeEach replaces that object
  // on every navigation and keeps it only in the route meta (`sharedMeta.isSales`). Reading the
  // top-level flag alone reported every sales journey as 'order' after its first step.
  const isSales = r.isSales === true || r.meta?.isSales === true;
  let module = 'order';
  if (isCenter) module = 'center';
  else if (isSales) module = 'sales';
  return { occasion, module };
}

function wizardDimensions(extra?: Record<string, string>): Record<string, string> {
  const { occasion, module } = wizardContext();
  // `module` is a suite-backend-only dimension; the center/sales properties drop it silently.
  return { occasion, module, ...(extra ?? {}) };
}

/**
 * Track one order-wizard funnel event under `order.create`.
 *
 * @param action the event action (e.g. `wizard_step_advanced`, `tariff_selected`).
 * @param detail the event's own value, the first part of the name: the step for
 *               `wizard_started`/`wizard_step_advanced`/`validation_error_shown`, the Tarif slug
 *               for `tariff_calculated`/`tariff_selected`, the Sparte for `prices_calculated`/
 *               `no_tariff_available`, the Abschlusskanal (`email` | `signature` | `document` |
 *               `document_password` | `letter`) for `order_submitted`/`completion_type_selected`.
 *               Occasion and origin are appended automatically: `<detail>|<occasion>|<origin>`.
 * @param dims   extra per-event dimensions, e.g. `{ 'object-uuid': productUuid }` at tariff steps.
 */
export function orderTrack(action: string, detail?: string, dims?: Record<string, string>): void {
  const { occasion, module } = wizardContext();
  ioTrack('order.create', action, `${detail ?? ''}|${occasion}|${module}`, wizardDimensions(dims));
}
